import { useState, useRef, useEffect, useCallback } from 'react';
import api from '../../utils/api';

/**
 * SpecialitySelect
 * Searchable speciality dropdown that:
 *   - Loads specialities from master-data once
 *   - Filters as user types
 *   - Offers "+ Add '[typed]' as new speciality" when no match found
 *
 * Props:
 *   value       string
 *   onChange    fn(value)
 *   placeholder string
 *   disabled    bool
 *   label       string  — defaults to "Speciality"
 */
export function SpecialitySelect({ value, onChange, placeholder = 'Search speciality…', disabled = false, label = 'Speciality' }) {
  const [items, setItems]   = useState([]);
  const [query, setQuery]   = useState('');
  const [open, setOpen]     = useState(false);
  const [adding, setAdding] = useState(false);
  const ref                 = useRef(null);

  useEffect(() => {
    api.get('/master-data/speciality')
      .then(r => setItems(r.data.data || []))
      .catch(() => {});
  }, []);

  // Sync display when value set externally (edit mode)
  useEffect(() => { if (value && query !== value) setQuery(value); }, [value]); // intentionally omits query

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? items.filter(i => i.value.toLowerCase().includes(query.toLowerCase())).slice(0, 30)
    : items.slice(0, 30);

  const exactMatch = items.some(i => i.value.toLowerCase() === query.trim().toLowerCase());
  const showAdd = query.trim().length > 1 && !exactMatch;

  const select = useCallback((item) => {
    onChange(item.value);
    setQuery(item.value);
    setOpen(false);
  }, [onChange]);

  const handleAdd = async () => {
    const newVal = query.trim();
    if (!newVal) return;
    setAdding(true);
    try {
      const r = await api.post('/master-data', { category: 'speciality', value: newVal, label: newVal });
      const created = r.data.data;
      setItems(prev => [...prev, created]);
      onChange(created.value);
      setQuery(created.value);
      setOpen(false);
    } catch {
      onChange(newVal);
      setOpen(false);
    } finally {
      setAdding(false);
    }
  };

  const clear = e => { e.stopPropagation(); onChange(''); setQuery(''); };

  return (
    <div className="form-group" style={{ margin: 0 }} ref={ref}>
      <label className="form-label">{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          className="form-control"
          value={query}
          placeholder={placeholder}
          disabled={disabled}
          onChange={e => { setQuery(e.target.value); onChange(''); setOpen(true); }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        {query && !disabled && (
          <span onClick={clear} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12 }}>✕</span>
        )}
        {open && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300,
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
            marginTop: 3, maxHeight: 220, overflowY: 'auto',
          }}>
            {filtered.length === 0 && !showAdd && (
              <div style={{ padding: '12px 14px', color: 'var(--text-muted)', fontSize: '0.82rem', textAlign: 'center' }}>No specialities found</div>
            )}
            {filtered.map(item => (
              <div
                key={item.id || item.value}
                onMouseDown={() => select(item)}
                style={{
                  padding: '8px 14px', cursor: 'pointer', fontSize: '0.875rem',
                  background: item.value === value ? 'var(--brand-purple-faint)' : 'transparent',
                  color: item.value === value ? 'var(--brand-purple)' : 'var(--text-primary)',
                  fontWeight: item.value === value ? 600 : 400,
                  borderBottom: '1px solid var(--border)',
                }}
                onMouseEnter={e => { if (item.value !== value) e.currentTarget.style.background = 'var(--bg)'; }}
                onMouseLeave={e => { if (item.value !== value) e.currentTarget.style.background = 'transparent'; }}
              >
                {item.value}
              </div>
            ))}
            {showAdd && (
              <>
                {filtered.length > 0 && <div style={{ borderTop: '1px solid var(--border)' }} />}
                <div
                  onMouseDown={handleAdd}
                  style={{ padding: '9px 14px', cursor: adding ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--brand-purple)', fontSize: '0.85rem', fontWeight: 500 }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--brand-purple-faint)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontSize: 16, lineHeight: 1 }}>＋</span>
                  {adding ? 'Adding…' : `Add "${query.trim()}" as new speciality`}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
